import { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";
import router from "@/router/index";
import { requiresPermission } from "@/functions/check";
import { PERMISSION_KEY } from "@/types/user";

export enum ROUTE_NAME_WEB {
    START = 'StartScreen',
    LOGIN = 'Login',
    PASSWORD_RESET = 'PasswordReset',
    PASSWORD_RESET_REQUEST = 'PasswordResetRequest',
    REGISTER = 'Register',
    REGISTER_SUCCESS = 'RegisterSuccess',
    LOCATION_INTRO = 'LocationIntro',
    LOCATION_CREATE_CHOICE = 'LocationCreateChoice',
    LOCATION_DATA = 'LocationData',
    LOCATION_SUPPLIER = 'LocationSupplier',
    LOCATION_SUPPLIER_CREATE = 'LocationSupplierCreate',
    LOCATION_CONFIRM = 'LocationConfirm',
    LOCATION_JOIN = 'LocationJoin',
    HOME = 'Home',
    DASHBOARD_HOME = 'DashboardHome',
    DASHBOARD_LIST = 'DashboardList',
    DASHBOARD_LIST_CONTENT = 'DashboardListContent',
    DASHBOARD_PROMOTION = 'DashboardPromotion',
    DASHBOARD_SETTINGS = 'DashboardSettings',
    DASHBOARD_MESSAGE = 'DashboardMessage',
    DASHBOARD_CHAT = 'DashboardChat',
    SETTING_PROFILE = 'SettingProfile',
    SETTING_PROFILE_CHANGE_PASSWORD = 'ProfileChangePassword',
    SETTING_LOCATION = 'SettingLocation',
    SETTING_ORDER = 'SettingOrder',
    SETTING_ORDER_NOTIFICATION = 'SettingOrderNotification',
    ORDER_DETAIL = 'OrderDetail',
    SETTING_TEAM = 'SettingTeam',
    SETTING_TEAM_INVITE = 'SettingTeamInvite',
    SETTING_DELIVERER = 'SettingDeliverer',
    SETTING_DELIVERER_SELECT = 'SettingDelivererSelect',
    SETTING_DELIVERER_PROFILE = 'SettingDelivererProfile',
    SETTING_DELIVERER_CONFIRM = 'SettingDelivererConfirm',
    SETTING_DELIVERER_ADD = 'SettingAddDeliverer',
    EDIT_CUSTOM_SUPPLIER = 'EditCustomSupplier',
    SETTING_NOTIFICATION = 'SettingNotification',
    SETTING_DISPLAY = 'SettingDisplay',
    SETTING_STATISTIC = 'SettingStatistic',
    SETTING_SUPPORT = 'SettingSupport',
    SETTING_LAW = 'SettingLaw',
    SETTING_INTRO_SLIDE = 'SettingIntroSlide',
    IMPRINT = 'Imprint',
    BLOCK_MOBILE_WEB = 'BlockMobileWeb'
}

const checkMobile = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const screen = window.screen
    if (screen.width < 1200) {
        return next({ name: ROUTE_NAME_WEB.BLOCK_MOBILE_WEB })
    }

    return next()
}

const dashboardChat: RouteRecordRaw[] = [
    {
        path: '/dashboard/:locationId/chat',
        name: ROUTE_NAME_WEB.DASHBOARD_CHAT,
        component: () => import('@/views/web/dashboard/DashboardChat.vue'),
        meta: {
            dashboard: true
        },
        beforeEnter: () => {
            if (!requiresPermission(PERMISSION_KEY.CHAT_READ)) {
                return { name: ROUTE_NAME_WEB.DASHBOARD_HOME }
            }
            return true
        }
    },
];

export const routes: RouteRecordRaw[] = [
    {
        path: '/',
        meta: {
            noAuth: true,
            noSidebar: true
        },
        redirect: '/start'
    },
    {
        path: '/redirectMobileUser',
        name: ROUTE_NAME_WEB.BLOCK_MOBILE_WEB,
        meta: {
            noAuth: true,
            noSidebar: true,
            force: true
        },
        component: () => import('@/views/web/RedirectMobileUser.vue')
    },
    {
        path: '/start',
        name: ROUTE_NAME_WEB.START,
        meta: {
            noAuth: true,
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "Start" */ '@/views/web/Start.vue'),
        beforeEnter: checkMobile
    },
    {
        path: '/login',
        name: ROUTE_NAME_WEB.LOGIN,
        meta: {
            noAuth: true,
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "Login" */ '@/views/web/auth/Login.vue'),
        beforeEnter: checkMobile
    },
    {
        path: '/reset-request',
        name: ROUTE_NAME_WEB.PASSWORD_RESET_REQUEST,
        meta: {
            noAuth: true,
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "ResetRequest" */ '@/views/web/auth/ResetRequest.vue')
    },
    {
        path: '/reset',
        name: ROUTE_NAME_WEB.PASSWORD_RESET,
        meta: {
            noAuth: true,
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "Reset" */ '@/views/web/auth/Reset.vue')
    },
    {
        path: '/register',
        name: ROUTE_NAME_WEB.REGISTER,
        meta: {
            noAuth: true,
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "Reset" */ '@/views/web/auth/Register.vue')
    },
    {
        path: '/register-success',
        name: ROUTE_NAME_WEB.REGISTER_SUCCESS,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "RegisterSuccess" */ '@/views/web/auth/RegisterSuccess.vue')
    },
    {
        path: '/location-intro',
        name: ROUTE_NAME_WEB.LOCATION_INTRO,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "LocationIntro" */ '@/views/web/location/LocationIntro.vue')
    },
    {
        path: '/location-choice',
        name: ROUTE_NAME_WEB.LOCATION_CREATE_CHOICE,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "LocationChoice" */ '@/views/web/location/LocationChoice.vue')
    },
    {
        path: '/location-join',
        name: ROUTE_NAME_WEB.LOCATION_JOIN,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "LocationJoin" */ '@/views/web/location/LocationJoin.vue')
    },
    {
        path: '/location-data',
        name: ROUTE_NAME_WEB.LOCATION_DATA,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "LocationData" */ '@/views/web/location/LocationData.vue')
    },
    {
        path: '/:locationId/location-supplier',
        name: ROUTE_NAME_WEB.LOCATION_SUPPLIER,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "LocationSupplier" */ '@/views/web/location/LocationSupplier.vue')
    },
    {
        path: '/:locationId/location-add-deliverer',
        name: ROUTE_NAME_WEB.LOCATION_SUPPLIER_CREATE,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "LocationSupplierCreate" */ '@/views/web/location/AddSupplier.vue')
    },
    {
        path: '/home',
        name: ROUTE_NAME_WEB.HOME,
        meta: {
            noSidebar: true
        },
        component: () => import(/* webpackChunkName: "Home" */ '@/views/web/Home.vue'),
        beforeEnter: checkMobile
    },
    {
        path: '/dashboard/:locationId',
        redirect: () => ({ name: ROUTE_NAME_WEB.DASHBOARD_HOME }),
        beforeEnter: checkMobile
    },
    {
        path: '/dashboard/:locationId/home',
        name: ROUTE_NAME_WEB.DASHBOARD_HOME,
        meta: {
            dashboard: true
        },
        component: () => import(/* webpackChunkName: "DashboardHome" */ '@/views/web/dashboard/DashboardHome.vue'),
        beforeEnter: checkMobile
    },
    {
        path: '/dashboard/:locationId/list',
        name: ROUTE_NAME_WEB.DASHBOARD_LIST,
        meta: {
            dashboard: true
        },
        component: () => import(/* webpackChunkName: "DashboardList" */ '@/views/web/dashboard/DashboardList.vue')
    },
    {
        path: '/dashboard/:locationId/list/:listId',
        name: ROUTE_NAME_WEB.DASHBOARD_LIST_CONTENT,
        meta: {
            dashboard: true
        },
        beforeEnter: (to, from, next) => {
            const listStore = useListStore()
            const supplierStore = useSupplierStore()

            if (!listStore.listFilter?.lastListId) {
                listStore.listFilter.tags = {}
                listStore.listFilter.category = {}
                listStore.listFilter.withQuantity = false
                listStore.listFilter.lastListId = to.params.listId as string
                return next()
            }

            const lastSupplierId = listStore.listById(listStore.listFilter.lastListId)?.supplierId

            if (!lastSupplierId) return next()

            const newSupplierId = listStore.listById(to.params.listId as string ?? '')?.supplierId ?? ''

            listStore.listFilter.lastListId = to.params.listId as string

            if (lastSupplierId !== newSupplierId) {
                listStore.listFilter.tags = {}
                listStore.listFilter.category = {}
                listStore.listFilter.withQuantity = false
            }

            if (newSupplierId !== supplierStore.catalogFilter.lastSupplierId) {
                supplierStore.catalogFilter.category = {}
            }

            supplierStore.catalogFilter.lastSupplierId = newSupplierId

            next()
        },
        component: () => import(/* webpackChunkName: "DashboardListContent" */ '@/views/web/dashboard/subviews/DashboardListContent.vue')
    },
    {
        path: '/dashboard/:locationId/promotion',
        name: ROUTE_NAME_WEB.DASHBOARD_PROMOTION,
        meta: {
            dashboard: true
        },
        component: () => import(/* webpackChunkName: "DashboardPromotion" */ '@/views/web/dashboard/DashboardPromotion.vue')
    },
    {
        path: '/dashboard/:locationId/message',
        name: ROUTE_NAME_WEB.DASHBOARD_MESSAGE,
        meta: {
            dashboard: true
        },
        component: () => import(/* webpackChunkName: "DashboardMessage" */ '@/views/web/dashboard/DashboardMessage.vue')
    },
    {
        path: '/dashboard/:locationId/settings',
        name: ROUTE_NAME_WEB.DASHBOARD_SETTINGS,
        redirect: to => {
            return { name: ROUTE_NAME_WEB.SETTING_PROFILE, params: { locationId: to.params.locationId } }
        },
        meta: {
            dashboard: true
        },
        component: () => import(/* webpackChunkName: "DashboardSettings" */ '@/views/web/dashboard/DashboardSettings.vue'),
        children: [
            {
                path: 'profile',
                name: ROUTE_NAME_WEB.SETTING_PROFILE,
                component: () => import(/* webpackChunkName: "Profile" */ '@/views/web/dashboard/subviews/setting/SettingProfile.vue')
            },
            {
                path: 'credentials',
                name: ROUTE_NAME_WEB.SETTING_PROFILE_CHANGE_PASSWORD,
                component: () => import(/* webpackChunkName: "ChangeCredentials" */ '@/views/web/dashboard/subviews/setting/ChangeCredentials.vue')
            },
            {
                path: 'location',
                name: ROUTE_NAME_WEB.SETTING_LOCATION,
                component: () => import(/* webpackChunkName: "SettingLocation" */ '@/views/web/dashboard/subviews/setting/SettingLocation.vue')
            },
            {
                path: 'order',
                name: ROUTE_NAME_WEB.SETTING_ORDER,
                component: () => import(/* webpackChunkName: "SettingOrder" */ '@/views/web/dashboard/subviews/setting/SettingOrder.vue')
            },
            {
                path: 'order-detail/:orderId',
                name: ROUTE_NAME_WEB.ORDER_DETAIL,
                component: () => import(/* webpackChunkName: "OrderDetail" */ '@/views/web/dashboard/subviews/setting/OrderDetail.vue')
            },
            {
                path: 'team',
                name: ROUTE_NAME_WEB.SETTING_TEAM,
                component: () => import(/* webpackChunkName: "SettingTeam" */ '@/views/web/dashboard/subviews/setting/SettingTeam.vue')
            },
            {
                path: 'supplier',
                name: ROUTE_NAME_WEB.SETTING_DELIVERER,
                component: () => import(/* webpackChunkName: "SettingSupplier" */ '@/views/web/dashboard/subviews/setting/SettingSupplier.vue')
            },
            {
                path: 'supplier-profile/:supplierId',
                name: ROUTE_NAME_WEB.SETTING_DELIVERER_PROFILE,
                component: () => import(/* webpackChunkName: "SettingSupplierProfile" */ '@/views/web/dashboard/subviews/setting/SettingSupplierProfile.vue')
            },
            {
                path: 'supplier-edit/:supplierId',
                name: ROUTE_NAME_WEB.EDIT_CUSTOM_SUPPLIER,
                component: () => import(/* webpackChunkName: "CustomSupplierEdit" */ '@/views/web/dashboard/subviews/setting/CustomSupplierEdit.vue')
            },
            {
                path: 'supplier-select',
                name: ROUTE_NAME_WEB.SETTING_DELIVERER_SELECT,
                component: () => import(/* webpackChunkName: "SettingSupplierSelect" */ '@/views/web/dashboard/subviews/setting/SettingSupplierSelect.vue')
            },
            {
                path: 'supplier-add',
                name: ROUTE_NAME_WEB.SETTING_DELIVERER_ADD,
                component: () => import(/* webpackChunkName: "SettingSupplierAdd" */ '@/views/web/dashboard/subviews/setting/SettingSupplierAdd.vue')
            },
            {
                path: 'team/invite',
                name: ROUTE_NAME_WEB.SETTING_TEAM_INVITE,
                component: () => import(/* webpackChunkName: "SettingTeamInvite" */ '@/views/web/dashboard/subviews/setting/SettingTeamInvite.vue')
            },
            {
                path: 'order-notification',
                name: ROUTE_NAME_WEB.SETTING_ORDER_NOTIFICATION,
                component: () => import(/* webpackChunkName: "OrderNotification" */ '@/views/web/dashboard/subviews/setting/OrderNotification.vue')
            },
            {
                path: 'notification',
                name: ROUTE_NAME_WEB.SETTING_NOTIFICATION,
                component: () => import(/* webpackChunkName: "NotificationSetting" */ '@/views/web/dashboard/subviews/setting/SettingNotification.vue')
            },
            {
                path: 'display',
                name: ROUTE_NAME_WEB.SETTING_DISPLAY,
                component: () => import(/* webpackChunkName: "SettingDisplay" */ '@/views/web/dashboard/subviews/setting/SettingDisplay.vue')
            },
            {
                path: 'support',
                name: ROUTE_NAME_WEB.SETTING_SUPPORT,
                component: () => import(/* webpackChunkName: "ContactSupport" */ '@/views/web/dashboard/subviews/setting/SettingSupport.vue')
            },
            {
                path: 'legal',
                name: ROUTE_NAME_WEB.SETTING_LAW,
                component: () => import(/* webpackChunkName: "SettingLegal" */ '@/views/web/dashboard/subviews/setting/SettingLegal.vue')
            },
            {
                path: 'app-tour',
                name: ROUTE_NAME_WEB.SETTING_INTRO_SLIDE,
                component: () => import(/* webpackChunkName: "AppTour" */ '@/views/web/dashboard/subviews/setting/AppTour.vue')
            }
        ]
    },
    ...dashboardChat
]
